import React from 'react';
import { CheckCircle, User as UserIcon, BookOpen, Award as AwardIcon, Cpu as CpuIcon, FileText, Briefcase } from 'lucide-react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText, 
  isFieldUpdating
} from '../common/resumeHelpers';
import { 
  UpdatingIndicator, 
  SectionTitle,
  ContactInfo,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  SkillSection,
  PaperSection,
  CoreCompetenciesSection,
  SummarySection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 1 - 기존 템플릿 (전문적이고 깔끔한 스타일)
const Template1 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-1 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <header className={`p-6 border-b border-gray-200 relative text-gray-800 transition-colors duration-300 ${isFieldUpdating('name', updatingField) || isFieldUpdating('title', updatingField) ? 'bg-green-50' : 'bg-gray-50'}`}>
        <div className="relative z-10">
          <h1 className={getPlaceholderClass(hasRealData.name, "text-3xl font-bold mb-2 uppercase text-gray-900")}>
            {currentData.name}
          </h1>
          <p className={getPlaceholderClass(hasRealData.title, "text-lg font-medium mb-4 text-blue-600")}>
            {currentData.title}
          </p>
          <ContactInfo
            phone={currentData.phone}
            email={currentData.email}
            address={currentData.address}
            socialLinks={currentData.socialLinks}
            hasRealData={hasRealData}
            isFieldUpdating={isFieldUpdating}
            updatingField={updatingField}
          />
        </div>
        {(isFieldUpdating('name', updatingField) || isFieldUpdating('title', updatingField)) && (
          <UpdatingIndicator text="기본 정보 반영 완료!" />
        )}
      </header>

      <main className="p-6 space-y-6">
        <section className={`transition-colors duration-300 ${isFieldUpdating('summary', updatingField) || isFieldUpdating('keyStrengths', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
          <SectionTitle icon={UserIcon} title={titles.introduction} />
          <SummarySection
            summary={currentData.summary}
            hasRealData={hasRealData}
            textClassName="text-sm mb-4 text-gray-700"
          />
          {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
            <div className="mt-5">
              <h3 className="text-base font-semibold text-gray-700 mb-3">{titles.coreCompetencies}</h3>
              <CoreCompetenciesSection
                keyStrengths={currentData.keyStrengths}
                hasRealData={hasRealData}
                bulletStyle={<CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${!hasRealData.keyStrengths ? 'text-gray-200' : 'text-blue-500'}`} />}
                textClassName="text-sm text-gray-700"
              />
            </div>
          )}
          {(isFieldUpdating('summary', updatingField) || isFieldUpdating('keyStrengths', updatingField)) && (
            <UpdatingIndicator text="프로필 및 핵심 역량 반영 완료!" />
          )}
        </section>

        <section className={`transition-colors duration-300 ${isFieldUpdating('experiences', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
          <SectionTitle icon={Briefcase} title={titles.experience} />
          <ExperienceSection
            experiences={currentData.experiences}
            hasRealData={hasRealData}
            datePosition="right"
            positionClassName="text-lg font-semibold text-gray-800"
            companyClassName="font-medium text-blue-600"
            dateClassName="text-sm text-gray-600"
            descriptionClassName="text-sm"
            projectTitleClassName="text-sm font-semibold text-gray-700"
            projectDescriptionClassName="text-sm"
            projectIndent="pl-2"
          />
          {isFieldUpdating('experiences', updatingField) && (
            <UpdatingIndicator text="경력 정보 반영 완료!" />
          )}
        </section>

        <section className={`transition-colors duration-300 ${isFieldUpdating('education', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
          <SectionTitle icon={BookOpen} title={titles.education} />
          <EducationSection
            education={currentData.education}
            hasRealData={hasRealData}
            datePosition="right"
            itemClassName="flex flex-col sm:flex-row justify-between sm:items-start mb-3 pb-3 border-b border-dotted border-gray-200 last:border-b-0 last:pb-0 last:mb-0"
            degreeClassName="text-base font-semibold text-gray-800"
            schoolClassName="text-sm text-gray-700"
            majorClassName="text-xs text-gray-400"
            dateClassName="text-xs sm:text-sm mt-1 sm:mt-0 whitespace-nowrap text-gray-600"
          />
          {isFieldUpdating('education', updatingField) && (
            <UpdatingIndicator text="학력 정보 반영 완료!" />
          )}
        </section>

        {hasRealData.papers && (
          <section className={`transition-colors duration-300 ${isFieldUpdating('papers', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
            <SectionTitle icon={FileText} title={titles.papers} />
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              itemClassName="mb-4 last:mb-0 pb-4 border-b border-dotted border-gray-200 last:border-b-0 last:pb-0"
              titleClassName="text-base font-semibold text-gray-800 leading-snug"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm text-gray-700"
              typeClassName="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
            />
            {isFieldUpdating('papers', updatingField) && (
              <UpdatingIndicator text="논문 정보 반영 완료!" />
            )}
          </section>
        )}

        <section className={`transition-colors duration-300 ${isFieldUpdating('certificates', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
          <SectionTitle icon={AwardIcon} title={titles.certificates} />
          <div className="space-y-2">
            {currentData.certificates.map((cert, index) => (
              <div 
                key={index} 
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 pb-2 border-b border-dotted border-gray-200 last:border-b-0 last:pb-0"
              >
                <div className="sm:col-span-5">
                  <h3 className={getPlaceholderClass(hasRealData.certificates, "text-sm font-semibold text-gray-800")}>
                    {cert.name}
                  </h3>
                </div>
                <div className="sm:col-span-4">
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-xs text-gray-600")}>
                    {cert.issuer}
                  </p>
                </div>
                <div className="sm:col-span-3 sm:text-right">
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-xs whitespace-nowrap text-gray-500")}>
                    {cert.date}
                  </p>
                </div>
              </div>
            ))}
          </div>
          {isFieldUpdating('certificates', updatingField) && (
            <UpdatingIndicator text="자격증 정보 반영 완료!" />
          )}
        </section>

        <section className={`transition-colors duration-300 ${isFieldUpdating('skills', updatingField) ? 'bg-green-50 p-4 rounded-lg -m-4' : ''}`}>
          <SectionTitle icon={CpuIcon} title={titles.skills} />
          <SkillSection
            skills={currentData.skills}
            hasRealData={hasRealData}
            variant="tags"
            tagClassName="px-3 py-1 text-sm rounded-full font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
          />
          {isFieldUpdating('skills', updatingField) && (
            <UpdatingIndicator text="기술 스택 반영 완료!" />
          )}
        </section>
      </main>
      <footer className="p-4 text-center text-xs text-gray-400 border-t border-gray-200">
        {titles.footer}
      </footer>
    </div>
  );
};

export default Template1;